import React, { useEffect, useState } from "react";
import { api, apiJson } from "@/api/client.js";
import {
  BIG_BREAKFAST,
  BUTCHER,
  CAMERAS,
  COMMS,
  CONVOYS,
  DISHES,
  DRINKS,
  ESSENTIALS,
  FREEZER,
  LUNCH,
  NO_NEW_POINTS,
  OTHER_MORNINGS,
  PERMIT_NOTE,
  PERMIT_URL,
  READERS,
  RECOVERY,
  ROAD_STOP,
  ROOMS,
  SEPAQ_MAIL_NOTE,
  TERRAIN_COUNT,
  TOILET_PAPER,
  WATER,
} from "@/content/huntBoard.js";
import { HUNT_SUPPERS } from "@/content/huntMeals.js";
import { supperIndexForDate } from "@/lib/huntDates.js";

export function phoneReady(hunter) {
  return Boolean(hunter?.installed && hunter?.sepaq_downloaded && hunter?.points_imported && hunter?.offline_ready);
}

export function NordQuestScore({ onOpen, compact = false }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api("/api/nordquest").then((body) => setRows(body.hunters || [])).catch(() => setRows([]));
  }, []);
  const ready = rows.filter(phoneReady).length;
  const total = rows.length || 6;
  if (compact) {
    return (
      <>
        <p className="og-fit__count"><strong data-testid="nordquest-ready">NordQuest {ready}/{total}</strong></p>
        <p className="og-fit__note">0 nouveau point</p>
        <button type="button" className="og-tile og-fit__push" data-testid="tile-nordquest" onClick={onOpen}>Préparer mon téléphone</button>
      </>
    );
  }
  return (
    <button type="button" className="og-board-accent" data-testid="tile-nordquest" onClick={onOpen}>
      <strong data-testid="nordquest-ready">NordQuest — ton téléphone est-il prêt ?</strong>
      <span>{ready} / {total}</span>
      <span className="og-board-accent__row">
        {rows.map((hunter) => {
          const score = ["installed", "points_imported", "offline_ready"].filter((key) => hunter[key]).length;
          return <em key={hunter.member_id}>{hunter.first_name} {score}/3{score === 3 ? " ✓" : ""}</em>;
        })}
      </span>
    </button>
  );
}

export function Readiness({ tasks }) {
  const hunters = ["mem_luc", "mem_eric", "mem_marcel", "mem_pierre", "mem_steve", "mem_dan"];
  const [nq, setNq] = useState([]);
  const [essentials, setEssentials] = useState([]);
  useEffect(() => {
    api("/api/nordquest").then((body) => setNq(body.hunters || [])).catch(() => setNq([]));
    api("/api/essentials").then((body) => setEssentials(body.hunters || [])).catch(() => setEssentials([]));
  }, []);

  function doneCount(title) {
    return hunters.filter((id) => tasks.some((task) => task.assignee_id === id && task.status === "done" && (task.title || "").startsWith(title))).length;
  }
  const permis = doneCount("Acheter / vérifier");
  const sepaq = doneCount("Compléter mon inscription");
  const phones = nq.filter(phoneReady).length;
  const people = essentials.filter((row) => row.done).length;
  const readyPeople = hunters.filter((id) => {
    const permit = tasks.some((task) => task.assignee_id === id && task.status === "done" && (task.title || "").startsWith("Acheter / vérifier"));
    const form = tasks.some((task) => task.assignee_id === id && task.status === "done" && (task.title || "").startsWith("Compléter mon inscription"));
    const phone = nq.some((row) => row.member_id === id && phoneReady(row));
    const kit = essentials.some((row) => row.member_id === id && row.done);
    return permit && form && phone && kit;
  }).length;

  const lines = [
    ["Permis", `${permis} / 6`],
    ["Inscription Sépaq", `${sepaq} / 6`],
    ["NordQuest", `${phones} / 6`],
    ["Véhicules", "Convois connus · 4 à confirmer"],
    ["Nourriture", "Soupers assignés"],
    ["Matériel commun", "Partiel · à confirmer au chargement"],
    ["Matériel personnel", `${people} / 6`],
  ];
  return (
    <section className="og-board-ready" data-surface="light" data-testid="readiness">
      <h3>{readyPeople} / 6 chasseurs prêts</h3>
      <ul>
        {lines.map(([label, value]) => (
          <li key={label}><span>{label}</span><strong>{value}</strong></li>
        ))}
      </ul>
    </section>
  );
}

const WIND_FROM = { N: 0, NE: 45, E: 90, SE: 135, S: 180, SW: 225, W: 270, NW: 315 };

export function WindMark({ dir, speed }) {
  const deg = WIND_FROM[String(dir || "").toUpperCase()] ?? 0;
  return (
    <p className="og-wind" data-testid="wind-mark">
      <span className="og-wind__arrow" style={{ transform: `rotate(${deg}deg)` }} aria-hidden="true">↑</span>
      Vent : {dir || "—"} {speed ?? "—"} km/h
    </p>
  );
}

export function WeatherCard({ compact = false, dense = false }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api("/api/weather").then(setData).catch(() => setData({ ok: false, place: "Secteur Desrochers" }));
  }, []);
  if (!data) return <p className="og-hint" data-testid={compact ? "weather-compact" : "weather-card"}>{compact ? "Météo…" : "Météo du territoire…"}</p>;
  if (!data.ok) {
    if (compact) return <p data-testid="weather-compact">Météo indisponible</p>;
    return (
      <section className="og-board-weather" data-testid="weather-card">
        <h3>Météo du territoire</h3>
        <p>Météo indisponible pour le moment.</p>
      </section>
    );
  }
  const now = data.current || {};
  const day = data.daily || {};
  if (dense) {
    return (
      <section data-testid="weather-card">
        <p className="og-fit__count" data-testid="weather-now">{now.temperature ?? "—"} °C</p>
        <p>Max {day.max ?? "—"} · min {day.min ?? "—"} · pluie {day.rain ?? "—"} %</p>
        <WindMark dir={now.wind_dir} speed={now.wind_speed} />
        <ul className="og-board-hours" data-testid="weather-hours">
          {(data.hourly || []).filter((_, index) => index % 3 === 0).slice(0, 4).map((hour) => (
            <li key={hour.hour}>{hour.hour} · {hour.temperature}° · {hour.wind_dir}</li>
          ))}
        </ul>
      </section>
    );
  }
  if (compact) {
    return (
      <div>
        <p className="og-fit__count" data-testid="weather-compact">
          {now.temperature ?? "—"} °C
          <span>pluie {day.rain ?? "—"} %</span>
        </p>
        <WindMark dir={now.wind_dir} speed={now.wind_speed} />
      </div>
    );
  }
  return (
    <section className="og-board-weather" data-testid="weather-card">
      <h3>Météo du territoire</h3>
      <p data-testid="weather-place">{data.place}</p>
      <WindMark dir={now.wind_dir} speed={now.wind_speed} />
      <p className="og-board-weather__now" data-testid="weather-now">
        {now.temperature ?? "—"} °C · max {day.max ?? "—"} · min {day.min ?? "—"} · pluie {day.rain ?? "—"} %
      </p>
      <p>Max {day.max ?? "—"} °C · min {day.min ?? "—"} °C</p>
      <ul className="og-board-hours" data-testid="weather-hours">
        {(data.hourly || []).filter((_, index) => index % 3 === 0).slice(0, 8).map((hour) => (
          <li key={hour.hour}>{hour.hour} · {hour.temperature}° · {hour.wind_dir} {hour.wind} · {hour.rain} %</li>
        ))}
      </ul>
    </section>
  );
}

export function DayPlanPanel({ day, admin }) {
  const [plan, setPlan] = useState(null);
  useEffect(() => {
    api(`/api/day-plan?day=${day}`).then((body) => {
      setPlan(body?.date ? body : { date: day, party: "", vehicle: "", spot: "", note: "", can_edit: admin });
    }).catch(() => setPlan({ date: day, party: "", vehicle: "", spot: "", note: "", can_edit: admin }));
  }, [day, admin]);
  if (!plan) return <p className="og-hint">Plan du jour…</p>;
  const empty = !plan.party && !plan.vehicle && !plan.spot && !plan.note;
  const editable = Boolean(plan.can_edit) && (!plan.closed || admin);
  async function save(event) {
    event.preventDefault();
    const body = {
      date: day,
      party: event.target.party.value,
      vehicle: event.target.vehicle.value,
      spot: event.target.spot.value,
      note: event.target.note.value,
    };
    const saved = await apiJson("/api/day-plan", body, "PUT");
    if (saved?.date) setPlan(saved);
  }
  return (
    <div data-testid="day-plan">
      <p>Valable pour cette journée seulement. Pas d’équipe fixe.</p>
      {empty ? <p data-testid="day-plan-empty">Pas encore décidé ce matin.</p> : (
        <ul>
          {plan.party ? <li>Qui part avec qui : {plan.party}</li> : null}
          {plan.vehicle ? <li>Véhicule : {plan.vehicle}</li> : null}
          {plan.spot ? <li>Zone : {plan.spot}</li> : null}
          {plan.note ? <li>{plan.note}</li> : null}
        </ul>
      )}
      {editable ? (
        <form className="og-board-form" onSubmit={save}>
          <input className="og-input" name="party" defaultValue={plan.party || ""} placeholder="Qui part avec qui" />
          <input className="og-input" name="vehicle" defaultValue={plan.vehicle || ""} placeholder="Véhicule" />
          <input className="og-input" name="spot" defaultValue={plan.spot || ""} placeholder="Zone ou spot" />
          <input className="og-input" name="note" defaultValue={plan.note || ""} placeholder="Note" />
          <button type="submit" className="og-admin-btn">Enregistrer le plan</button>
        </form>
      ) : null}
    </div>
  );
}

export function SupperBanner({ iso }) {
  const index = supperIndexForDate(iso);
  const supper = index >= 0 ? HUNT_SUPPERS[index] : null;
  return (
    <p className="og-board-supper" data-testid="supper-banner">
      {supper ? `Souper ce soir : ${supper.who} — ${supper.dish}` : "Souper de cette journée : à confirmer"}
      <span>{FREEZER}</span>
    </p>
  );
}

export function RoomsPanel() {
  return (
    <div data-testid="rooms-panel">
      <ul className="og-meal-list">
        {ROOMS.map((room) => (
          <li key={room.id} data-testid={`room-${room.id}`}>
            <strong>{room.title}</strong>
            <span>{room.who}</span>
          </li>
        ))}
      </ul>
      <h3>Eau</h3>
      <ul>{WATER.map((line) => <li key={line}>{line}</li>)}</ul>
      <p>{TOILET_PAPER}</p>
      <p>{DISHES}</p>
    </div>
  );
}

export function VehicleFacts() {
  return (
    <div data-testid="vehicle-facts">
      {CONVOYS.map((convoy) => (
        <section key={convoy.id}>
          <h3>{convoy.title}</h3>
          <ul>{convoy.lines.map((line) => <li key={line}>{line}</li>)}</ul>
        </section>
      ))}
      <p>{ROAD_STOP}</p>
      <p>{TERRAIN_COUNT}</p>
    </div>
  );
}

export function GearFacts({ meId, admin }) {
  const [pack, setPack] = useState(null);
  useEffect(() => {
    api("/api/essentials").then(setPack).catch(() => setPack({ hunters: [], items: ESSENTIALS.map(([id]) => id) }));
  }, []);
  const mine = (pack?.hunters || []).find((row) => row.member_id === meId) || { checks: {}, can_edit: false };

  async function toggle(key) {
    if (!mine.can_edit && !admin) return;
    const checks = { ...(mine.checks || {}), [key]: !mine.checks?.[key] };
    setPack((current) => ({
      ...current,
      hunters: (current?.hunters || []).map((row) => (row.member_id === meId ? { ...row, checks, done: ESSENTIALS.every(([id]) => checks[id]) } : row)),
    }));
    try {
      await apiJson(`/api/essentials/${meId}`, { checks: { [key]: checks[key] } }, "PUT");
    } catch {
      /* la case reste pour cette séance */
    }
  }

  return (
    <div data-testid="gear-facts">
      <h3>Communications</h3>
      <ul>{COMMS.map((line) => <li key={line}>{line}</li>)}</ul>
      <h3>Caméras</h3>
      <p>{CAMERAS}</p>
      <p>{READERS}</p>
      <h3>Si on récolte un orignal</h3>
      <ul>{RECOVERY.map((line) => <li key={line}>{line}</li>)}</ul>
      <p data-testid="butcher-plan">{BUTCHER.name} — {BUTCHER.who}. {BUTCHER.note}</p>
      <h3>Mes essentiels</h3>
      <ul className="og-meal-list" data-testid="essentials">
        {ESSENTIALS.map(([id, label]) => (
          <li key={id}>
            <label>
              <input type="checkbox" checked={Boolean(mine.checks?.[id])} disabled={!mine.can_edit && !admin} onChange={() => toggle(id)} />
              {label}
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function MealExtras() {
  return (
    <>
      <section data-testid="breakfasts">
        <h3>Déjeuners préparés par Josée</h3>
        <p>{BIG_BREAKFAST}</p>
        <p>{OTHER_MORNINGS}</p>
      </section>
      <section data-testid="lunches">
        <h3>Lunchs</h3>
        <p>{LUNCH}</p>
      </section>
      <section>
        <h3>Boissons</h3>
        <p>{DRINKS}</p>
      </section>
    </>
  );
}

export function SepaqInscription() {
  return (
    <section data-testid="sepaq-inscription">
      <h3>Inscription Sépaq</h3>
      <p>{SEPAQ_MAIL_NOTE}</p>
      <h3>Permis</h3>
      <p>{PERMIT_NOTE}</p>
      <a className="og-btn og-btn--primary" href={PERMIT_URL} target="_blank" rel="noreferrer" data-testid="permit-link">Acheter mon permis</a>
    </section>
  );
}

export function PointVersions() {
  return (
    <section data-testid="nq-versions">
      <h3>Versions</h3>
      <p>Version 1 — 9 points d’Éric, prospection du 29 août.</p>
      <p>{NO_NEW_POINTS}</p>
    </section>
  );
}

export function NewPointsBanner({ onOpen }) {
  const [group, setGroup] = useState(null);
  useEffect(() => {
    api("/api/nordquest/group").then((body) => {
      if (body && Array.isArray(body.hunters)) {
        window.localStorage.setItem("orignal2026.nordquest.group", JSON.stringify(body));
        setGroup(body);
      }
    }).catch(() => {
      try {
        const cached = window.localStorage.getItem("orignal2026.nordquest.group");
        if (cached) setGroup(JSON.parse(cached));
      } catch { /* dernière version indisponible */ }
    });
  }, []);
  const latest = group?.latest;
  const offer = group?.mine_offer || "";
  const fresh = offer
    ? offer === "delta" || offer === "repair" || (offer === "complete" && latest?.code !== "1")
    : latest && latest.code !== "1" && group.mine_installed !== latest.code;
  const freshLabel = offer === "repair"
    ? "Réparer ma carte"
    : offer === "complete"
      ? "Installer la carte complète"
      : offer === "delta"
        ? "Mise à jour disponible"
        : "Nouvelle carte disponible";
  return (
    <button type="button" className="og-board-accent" data-testid="nordquest-update" onClick={onOpen}>
      <strong>NordQuest</strong>
      <span>Version {latest?.code || "1"}</span>
      {fresh ? (
        <>
          <span>{freshLabel}</span>
          <span>Version {latest.code}</span>
          <span>{latest.point_count} points</span>
          <span>{offer === "delta" ? `${group.mine_delta_count || latest.new_count || 0} nouveaux` : `${latest.point_count} points attendus`}</span>
          <span>{offer === "repair" ? "Réparer ma carte" : "Ouvrir la carte du groupe"}</span>
        </>
      ) : (
        <>
          <span>0 nouveau point à télécharger</span>
          <span>Mettre ma carte à jour</span>
        </>
      )}
    </button>
  );
}
